import { Alert, AlertDescription } from '@/components/ui-kit/alert';
import { cn } from '@/lib/utils';
import { AlertTriangle, Bot, MapPin, Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LegalDisclaimerProps {
  variant?: 'compact' | 'full';
  className?: string;
}

export function LegalDisclaimer({ variant = 'full', className = '' }: LegalDisclaimerProps) {
  const { t } = useTranslation();

  if (variant === 'compact') {
    const iconClass = 'h-3 w-3 mt-0.5 shrink-0 text-[hsl(var(--brand-primary))]';

    return (
      <div
        className={cn(
          'space-y-1 rounded-lg border border-[hsl(var(--brand-border))] bg-[hsl(var(--brand-surface))] px-4 py-4 text-xs text-[hsl(var(--brand-muted))] shadow-sm',
          className
        )}
      >
        <p className="flex items-start gap-1.5">
          <AlertTriangle className={iconClass} />
          <span>
            <strong className="text-[hsl(var(--brand-primary))]">
              {t('LEGALDISCLAIMER_NOTLEGALADVICE')}
            </strong>{' '}
            {t('LEGALDISCLAIMER_NOTLEGALADVICETEXT')}
          </span>
        </p>
        <p className="flex items-start gap-1.5">
          <MapPin className={iconClass} />
          <span>
            <strong className="text-[hsl(var(--brand-primary))]">
              {t('LEGALDISCLAIMER_SWITZERLANDBASED')}
            </strong>{' '}
            {t('LEGALDISCLAIMER_SWITZERLANDBASEDTEXT')}
          </span>
        </p>
        <p className="flex items-start gap-1.5">
          <Bot className={iconClass} />
          <span>
            <strong className="text-[hsl(var(--brand-primary))]">
              {t('LEGALDISCLAIMER_AIGENERATED')}
            </strong>{' '}
            {t('LEGALDISCLAIMER_AIGENERATEDTEXT')}
          </span>
        </p>
      </div>
    );
  }

  return (
    <Alert
      className={cn(
        'border-2 border-[hsl(var(--brand-border))] bg-[hsl(var(--brand-surface))] text-[hsl(var(--brand-muted))] shadow-sm',
        className
      )}
    >
      <AlertDescription>
        <div className="flex items-start gap-4">
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-[hsl(var(--brand-primary))]">
              <Scale className="h-6 w-6 text-[hsl(var(--brand-primary))]" />{' '}
              <span> {t('LEGALDISCLAIMER_IMPORTANTLEGALDISCLAIMER')}</span>
            </h3>

            <div className="space-y-3 text-sm leading-relaxed text-[hsl(var(--brand-muted))]">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-primary))]" />
                <p>
                  <strong className="font-semibold text-[hsl(var(--brand-primary))]">
                    {t('LEGALDISCLAIMER_NOTLEGALADVICE')}
                  </strong>{' '}
                  {t('LEGALDISCLAIMER_NOTLEGALADVICETEXT')}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-primary))]" />
                <p>
                  <strong className="font-semibold text-[hsl(var(--brand-primary))]">
                    {t('LEGALDISCLAIMER_JURISDICTIONDISCLAIMER')}
                  </strong>{' '}
                  {t('LEGALDISCLAIMER_JURISDICTIONDISCLAIMERTEXT')}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <Bot className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-primary))]" />
                <p>
                  <strong className="font-semibold text-[hsl(var(--brand-primary))]">
                    {t('LEGALDISCLAIMER_AIGENERATED')}
                  </strong>{' '}
                  {t('LEGALDISCLAIMER_AIGENERATEDTEXT')}
                </p>
              </div>

              <div className="flex items-start gap-3">
                <Scale className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--brand-primary))]" />
                <p>
                  <strong className="font-semibold text-[hsl(var(--brand-primary))]">
                    {t('LEGALDISCLAIMER_PROFESSIONALREVIEWREQUIRED')}
                  </strong>{' '}
                  {t('LEGALDISCLAIMER_PROFESSIONALREVIEWREQUIREDTEXT')}
                </p>
              </div>
            </div>

            <div className="border-t border-[hsl(var(--brand-border))] pt-3">
              <p className="text-xs font-medium text-[hsl(var(--brand-muted))]">
                {t('LEGALDISCLAIMER_ACKNOWLEDGMENT')}
              </p>
            </div>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}
